import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useGetData } from "@/hooks/useGetData";
import AxiosInstance from "@/utils/AxiosInstance";
import { getPromise, getPromiseToast, formatDate, formatDateTime24, formatDateTime12 } from "@/utils/helpers";
import LoaderWrapper from "@/pages/LoaderWrapper";
import { Smartphone, Unlink } from "lucide-react";
import TableWithSearch from "@/components/TableWithSearch";
import { Separator } from "@/components/ui/separator";
import {useAuth} from "@/contexts/use-auth";
import { COMMON_TIMEZONES, FACILITY_TIMEZONES } from "@/utils/CommonData";

interface Device {
  _id: string;
  deviceName: string;
  deviceType: string;
  serialNumber: string;
  status: string;
  [key: string]: any;
}

interface PatientDeviceProps {
  patientId: string;
  hideMapButton?: boolean;
}

export default function PatientDevice({ patientId, hideMapButton = false }: PatientDeviceProps) {
  const [isMapDeviceDialogOpen, setIsMapDeviceDialogOpen] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [patientDevices, setPatientDevices] = useState<Device[]>([]);
  const { user, hasModuleAdminAccess } = useAuth();
  const isPatient = user?.role === "patient";
  const isProviderOrPractitioner =
    user?.role?.toLowerCase() === "provider" ||
    user?.role?.toLowerCase() === "practitioner";
  const isProviderOrPractitionerManager = user?.role === 'manager' && (user?.managerScope === 'provider' || user?.managerScope === 'practitioner');  const isAdmin = hasModuleAdminAccess("canAccessPatients") ;

  // Fetch devices for mapping (available devices)
  const {
    apiData: devicesData,
    loading: devicesLoading,
    err: devicesErr,
    fetchData: fetchDevices,
  } = useGetData(
    "/devices",
    {},
    false,
    isMapDeviceDialogOpen, // Only fetch when dialog is open
    () => {}
  );

  // Fetch devices already mapped to this patient
  const {
    apiData: patientDevicesData,
    loading: patientDevicesLoading,
    err: patientDevicesErr,
    fetchData: fetchPatientDevices,
  } = useGetData(
    "/devices",
    { patientId },
    false,
    !!patientId, // Fetch when patientId is available
    (err, data) => {
      if (data && !err) {
        setPatientDevices(data?.data?.devices || []);
      }
    }
  );

  const devices: Device[] = devicesData?.data?.devices || [];
  // console.log({devicesData});
  const commonTimeZoneData: string =
    patientDevicesData?.data?.resolvedTimezone ??
    devicesData?.data?.resolvedTimezone ??
    "";
  const commonTimeZone = FACILITY_TIMEZONES.find(tz => tz.value === commonTimeZoneData)?.label || "";
    // console.log({patientDevicesData});
    // console.log({devicesData});

  const handleMapDevice = async () => {
    if (!selectedDeviceId) {
      toast.error("Please select a device");
      return;
    }

    if (!patientId) {
      toast.error("Patient ID is missing");
      return;
    }

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setBtnLoading(true);
      const { data } = await AxiosInstance().post("/devices/map", {
        deviceId: selectedDeviceId,
        patientId: patientId,
      });

      resolveFn("Device mapped successfully");
      setIsMapDeviceDialogOpen(false);
      setSelectedDeviceId("");
      // Refresh patient devices list
      fetchPatientDevices();
    } catch (error: any) {
      rejectFn(error);
      toast.error(
        error?.response?.data?.message || "Failed to map device"
      );
    } finally {
      setBtnLoading(false);
    }
  };

  const handleDisassociate = async (device: Device) => {
    if (!device._id) {
      toast.error("Device ID is missing");
      return;
    }

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setBtnLoading(true);
      await AxiosInstance().post("/devices/disassociate", {
        deviceId: device._id,
      });

      resolveFn("Device disassociated successfully");
      // Refresh patient devices list
      fetchPatientDevices();
    } catch (error: any) {
      rejectFn(error);
      toast.error(
        error?.response?.data?.message || "Failed to disassociate device"
      );
    } finally {
      setBtnLoading(false);
    }
  };

  // Define table headers for device listing
  const deviceTableHeaders = [
    { id: "deviceName", label: "Device Name", isSortable: true },
    { id: "deviceType", label: "Device Type", isSortable: true },
    { id: "manufacturer", label: "Manufacturer", isSortable: true },
    { id: "model", label: "Model", isSortable: true },
    { id: "imei", label: "IMEI Number", isSortable: false },
    { id: "serialNumber", label: "Serial Number", isSortable: true },
    { id: "commonTimeZone", label: "Time Zone", isSortable: false },
    { id: "status", label: "Status", isSortable: true },
    { id: "assignedAt", label: "Assigned At", isSortable: true },
  ];

  return (
    <div className="border-grey border-[0.3px] rounded-[5px]">
      {!hideMapButton && (
        <div className="flex items-center justify-between p-4">
          <h2 className="flex items-center gap-2 teachers font-bold text-xl">
            <Smartphone className="w-5 h-5" />
            Devices
          </h2>
          {/* <Button onClick={() => setIsMapDeviceDialogOpen(true)}>
            Map the device
          </Button> */}
        </div>
      )}
      {!hideMapButton && <Separator className="bg-grey" />}


      {/* <Separator className="bg-grey" /> */}
      <div className="p-4">
        <TableWithSearch
          apiDataKey="devices"
          isColumnSelection
          columnPreferenceKey={`patient-devices-${patientId}`}
          url="/devices"
          columnHeaders={deviceTableHeaders}
          data={patientDevices}
          setData={setPatientDevices}
          fetchCondition={!!patientId}
          searchPlaceholder="Search devices..."
          heading=""
          isActionColumn={false}
          handleAction={() => {}}
          moreActions={isAdmin && !isProviderOrPractitionerManager ? [
            {
              label: "Disassociate",
              icon: <Unlink className="h-4 w-4" />,
              onClick: handleDisassociate,
              isShow: () => true,
            },
          ] : []}
          isIncludeSearch
          params={{ patientId }}
          viewData={[...patientDevices]
            .sort((a, b) => {
              const aTime = a?.assignedAt ? new Date(a.assignedAt).getTime() : 0;
              const bTime = b?.assignedAt ? new Date(b.assignedAt).getTime() : 0;
              return bTime - aTime;
            })
            .map((device) => ({
            ...device,
            commonTimeZone,
            deviceType: device.deviceType?.replace(/_/g, " ") || "",
            status: device.status?.replace(/([A-Z])/g, " $1").trim() || "",
            assignedAt: device.assignedAt
              ? formatDateTime12(device.assignedAt)
              : "",
          }))}
          filtersOptions={[]}
          filters={{}}
          setFilters={() => {}}
          clickRow={() => {}}
          isIgnoreSearchParams={true}
        />
      </div>

      <Dialog
        open={isMapDeviceDialogOpen}
        onOpenChange={(open) => {
          setIsMapDeviceDialogOpen(open);
          if (!open) {
            setSelectedDeviceId("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Map Device</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <LoaderWrapper loading={devicesLoading} error={devicesErr}>
              <div className="space-y-2">
                <Label htmlFor="device-select">
                  Select Device <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={selectedDeviceId}
                  onValueChange={setSelectedDeviceId}
                >
                  <SelectTrigger id="device-select" className="w-full">
                    <SelectValue placeholder="Select a device" />
                  </SelectTrigger>
                  <SelectContent>
                    {devices.length > 0 ? (
                      devices.map((device) => (
                        <SelectItem key={device._id} value={device._id}>
                          {device?.imei ? `${device.imei} (${device.deviceName})` : device.deviceName}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-devices" disabled>
                        No devices available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </LoaderWrapper>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsMapDeviceDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                disabled={btnLoading || !selectedDeviceId}
                onClick={handleMapDevice}
              >
                Map
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

